import type { LessonRecap } from '../model/types/ai-summarize/interfaces'

import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

dayjs.extend(duration)

/**
 * Safely parse the lesson-recap `summary` JSON string coming from the API.
 * Returns null when the payload is missing or malformed.
 */
export const parseLessonRecap = (raw: string | null): LessonRecap | null => {
  if (!raw) return null

  try {
    return JSON.parse(raw) as LessonRecap
  } catch {
    return null
  }
}

/**
 * Normalize a recap payload that may arrive as an object or a JSON string.
 */
export const resolveLessonRecap = (raw: LessonRecap | string | null | undefined): LessonRecap | null => {
  if (!raw) return null

  return typeof raw === 'string' ? parseLessonRecap(raw) : raw
}

/**
 * Convert a `MM:SS` (or `HH:MM:SS`) timestamp into seconds for video seeking.
 * Returns 0 for malformed input.
 */
export const timestampToSeconds = (timestamp: string): number => {
  const parts = timestamp.split(':').map(Number)

  if (parts.some(Number.isNaN)) return 0

  return parts.reduce((acc, part) => acc * 60 + part, 0)
}

/**
 * Format the lesson duration seconds into a `48 min 52 sec` string.
 */
export const formatLessonDuration = (seconds: number | null): string | null => {
  if (seconds == null) return null

  const time = dayjs.duration(seconds, 'seconds')

  return `${Math.floor(time.asMinutes())} min ${time.seconds()} sec`
}

/**
 * Format the kid speaking time into a `8 min / 16%` string
 * (percentage of the total lesson duration).
 */
export const formatSpeakingTime = (speakSeconds: number | null, totalSeconds: number | null): string | null => {
  if (speakSeconds == null || totalSeconds == null) return null

  const time = dayjs.duration(speakSeconds, 'seconds')
  const percent = totalSeconds > 0 ? Math.round((speakSeconds / totalSeconds) * 100) : 0

  return `${Math.floor(time.asMinutes())} min / ${percent}%`
}
